import React, { useState, useEffect, Fragment } from 'react';
import DataTable from 'react-data-table-component';
import { toast } from 'react-toastify';
import API from '../../services/API';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import Select from 'react-select';
import { useNavigate } from "react-router-dom";

const DEFAULT_FLOW_STEPS = [
    {
        key: "photo",
        type: "photo",
        label: "Take Photo"
    }
];

const STEP_TYPE_OPTIONS = [
    { value: "scan", label: "Scan Student" },
    { value: "photo", label: "Capture Photo" },
    { value: "qr", label: "Scan QR" },
];

const FLOW_PRESETS = [
    {
        label: "Single Photo",
        value: "single_photo",
        flow_mode: "single_step",
        flow_steps: [
            { key: "photo", type: "photo", label: "Take Photo" }
        ]
    },
    {
        label: "Scan Only",
        value: "scan_only",
        flow_mode: "multi_step",
        flow_steps: [
            { key: "scan", type: "scan", label: "Scan Student" }
        ]
    },
    {
        label: "Scan → Photo",
        value: "scan_photo",
        flow_mode: "multi_step",
        flow_steps: [
            { key: "scan", type: "scan", label: "Scan Student" },
            { key: "checkin_photo", type: "photo", label: "Check-in Photo" }
        ]
    },
    {
        label: "Scan → QR",
        value: "scan_qr",
        flow_mode: "multi_step",
        flow_steps: [
            { key: "scan", type: "scan", label: "Scan Student" },
            { key: "event_qr", type: "qr", label: "Scan Event QR" }
        ]
    },
    {
        label: "Scan → QR → Photo",
        value: "scan_qr_photo",
        flow_mode: "multi_step",
        flow_steps: [
            { key: "scan", type: "scan", label: "Scan Student" },
            { key: "event_qr", type: "qr", label: "Scan Event QR" },
            { key: "proof_photo", type: "photo", label: "Proof Photo" }
        ]
    },
    {
        label: "Scan → 2 Photos",
        value: "scan_two_photos",
        flow_mode: "multi_step",
        flow_steps: [
            { key: "scan", type: "scan", label: "Scan Student" },
            { key: "checkin_photo", type: "photo", label: "Check-in Photo" },
            { key: "verification_photo", type: "photo", label: "Verification Photo" }
        ]
    }
];

function safeParseFlowSteps(value) {
    if (!value) return DEFAULT_FLOW_STEPS;

    let parsed = value;

    if (typeof value === "string") {
        try {
            parsed = JSON.parse(value);
        } catch (e) {
            parsed = value
                .split(",")
                .map(x => x.trim())
                .filter(Boolean);
        }
    }

    if (!Array.isArray(parsed)) return DEFAULT_FLOW_STEPS;

    return parsed
        .map((step, index) => {
            if (typeof step === "string") {
                const type = step.trim().toLowerCase();
                return {
                    key: index === 0 ? type : `${type}_${index + 1}`,
                    type,
                    label: type.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())
                };
            }

            if (typeof step === "object" && step !== null) {
                const type = String(step.type || "photo").toLowerCase();
                const key = String(step.key || `${type}_${index + 1}`)
                    .toLowerCase()
                    .replace(/\s+/g, "_");

                return {
                    key,
                    type,
                    label: step.label || key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()),
                    description: step.description || ""
                };
            }

            return null;
        })
        .filter(step => step && ["scan", "photo", "qr"].includes(step.type));
}

function makeSafeKey(label, type, existingKeys = []) {
    const base = String(label || type || "step")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "") || "step";

    let key = base;
    let counter = 2;

    while (existingKeys.includes(key)) {
        key = `${base}_${counter}`;
        counter += 1;
    }

    return key;
}

const CustomAttendanceTypePage = () => {
    const [attendanceTypes, setAttendanceTypes] = useState([]);
    const [isTypeModalOpen, setIsTypeModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    const [selectedType, setSelectedType] = useState(null);
    const [formTypeName, setFormTypeName] = useState('');
    const [formTypeDesc, setFormTypeDesc] = useState('');
    const [formTypeActive, setFormTypeActive] = useState(1);

    const [selectedDeleteType, setSelectedDeleteType] = useState(null);
    const [formRequiresNotif, setFormRequiresNotif] = useState(0);
    const [formNotifTime, setFormNotifTime] = useState(0);
    const [teachers, setTeachers] = useState([]);
    const [selectedTeachers, setSelectedTeachers] = useState([]);

    const [formFlowMode, setFormFlowMode] = useState("single_step");
    const [formFlowSteps, setFormFlowSteps] = useState(DEFAULT_FLOW_STEPS);

    const navigate = useNavigate();

    const handleView = (row) => {
        navigate(`/customAttendanceDetail?id=${row.type_id}`);
    };
const renderFlowSummary = (row) => {
    const steps = safeParseFlowSteps(row.flow_steps);
    const isMulti = row.flow_mode === "multi_step";

    return (
        <div className="flow-cell">
            <span className={`flow-pill ${isMulti ? "multi" : "single"}`}>
                {isMulti ? "Multi-step" : "Single photo"}
            </span>

            <div className="flow-steps">
                {steps.map(step => step.label || step.key).join(" → ")}
            </div>
        </div>
    );
};

const columns = [
    {
        name: "Name",
        selector: row => row.name,
        sortable: true,
        width: "190px",
        wrap: true,
    },
    {
        name: "Description",
        selector: row => row.description || "-",
        sortable: false,
        grow: 1,
        minWidth: "240px",
        wrap: true,
    },
    {
        name: "Active",
        cell: row => (
            <span className={`status-pill ${row.is_active === 1 ? "active" : "inactive"}`}>
                {row.is_active === 1 ? "Active" : "Inactive"}
            </span>
        ),
        sortable: true,
        width: "110px",
        center: true,
    },
    {
        name: "Actions",
        cell: (row) => (
            <div className="action-cell">
                <button
                    className="btn btn-sm btn-outline-info"
                    onClick={() => handleView(row)}
                >
                    View
                </button>

                <button
                    className="btn btn-sm btn-outline-warning"
                    onClick={() => openEditTypeModal(row)}
                >
                    Edit
                </button>

                <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => openDeleteModal(row)}
                >
                    Delete
                </button>
            </div>
        ),
        width: "350px",
        center: true,
        ignoreRowClick: true,
    }
];

    const fetchTeachers = () => {
        const headers = {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'token': localStorage.getItem('token'),
        };

        API.get('/portal/getTeachers', { headers })
            .then((response) => {
                if (response.data.response_code === 1000) {
                    const teacherOptions = response.data.data.map((teacher) => ({
                        label: teacher.full_name,
                        value: teacher.teacher_uuid,
                    }));

                    setTeachers(teacherOptions);
                } else {
                    toast.error(`${response.data.message}`, { autoClose: 5000 });
                }
            })
            .catch(() => {
                toast.error("Error fetching teachers", { autoClose: 5000 });
            });
    };

    const fetchAttendanceTypes = () => {
        const headers = {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'token': localStorage.getItem('token'),
        };

        API.get('/customAttendance/type?is_portal=true', { headers })
            .then((res) => {
                if (res.data.response_code === 1000) {
                    setAttendanceTypes(res.data.data || []);
                } else {
                    toast.error(res.data.message);
                }
            })
            .catch(() => toast.error("Error fetching attendance types"));
    };

    useEffect(() => {
        fetchAttendanceTypes();
    }, []);

    const resetFlow = () => {
        setFormFlowMode("single_step");
        setFormFlowSteps(DEFAULT_FLOW_STEPS);
    };

    const openAddTypeModal = () => {
        setSelectedType(null);
        setFormTypeName('');
        setFormTypeDesc('');
        setFormTypeActive(1);
        setFormRequiresNotif(0);
        setFormNotifTime(0);
        setSelectedTeachers([]);
        resetFlow();
        fetchTeachers();
        setIsTypeModalOpen(true);
    };

    const openEditTypeModal = (type) => {
        setSelectedType(type);
        setFormTypeName(type.name);
        setFormTypeDesc(type.description || '');
        setFormTypeActive(Number(type.is_active ?? 1));
        setFormRequiresNotif(Number(type.requires_notification || 0));
        setFormNotifTime(Number(type.notification_time || 0));

        setFormFlowMode(type.flow_mode || "single_step");
        setFormFlowSteps(safeParseFlowSteps(type.flow_steps));

        fetchTeachers();

        const formattedSelected = (type.notify_teachers || []).map(t => ({
            label: t.teacher_name || t.full_name || "Unnamed Teacher",
            value: t.teacher_uuid || t.user_uuid || t.value
        }));

        setSelectedTeachers(formattedSelected);
        setIsTypeModalOpen(true);
    };

    const closeTypeModal = () => setIsTypeModalOpen(false);

    const applyPreset = (presetValue) => {
        const preset = FLOW_PRESETS.find(x => x.value === presetValue);
        if (!preset) return;

        setFormFlowMode(preset.flow_mode);
        setFormFlowSteps(preset.flow_steps);
    };

    const handleFlowModeChange = (value) => {
        setFormFlowMode(value);

        if (value === "single_step") {
            setFormFlowSteps(DEFAULT_FLOW_STEPS);
        } else {
            setFormFlowSteps([
                { key: "scan", type: "scan", label: "Scan Student" },
                { key: "checkin_photo", type: "photo", label: "Check-in Photo" }
            ]);
        }
    };

    const handleAddStep = () => {
        const existingKeys = formFlowSteps.map(step => step.key);
        const key = makeSafeKey("new_photo", "photo", existingKeys);

        setFormFlowSteps([
            ...formFlowSteps,
            {
                key,
                type: "photo",
                label: "New Photo Step"
            }
        ]);
    };

    const handleRemoveStep = (index) => {
        if (formFlowMode === "single_step") {
            toast.warning("Single-step flow must keep the photo step.");
            return;
        }

        const next = formFlowSteps.filter((_, i) => i !== index);

        if (next.length === 0) {
            toast.error("Flow must have at least one step.");
            return;
        }

        setFormFlowSteps(next);
    };

    const updateFlowStep = (index, patch) => {
        setFormFlowSteps(prev => {
            const next = [...prev];
            const current = { ...next[index], ...patch };

            if (patch.label && !patch.key) {
                const otherKeys = next
                    .filter((_, i) => i !== index)
                    .map(step => step.key);

                current.key = makeSafeKey(patch.label, current.type, otherKeys);
            }

            if (patch.type && !current.label) {
                current.label = patch.type.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
            }

            next[index] = current;
            return next;
        });
    };

    const validateFlow = () => {
        if (formFlowMode === "single_step") {
            return {
                ok: true,
                steps: DEFAULT_FLOW_STEPS
            };
        }

        if (!Array.isArray(formFlowSteps) || formFlowSteps.length === 0) {
            return {
                ok: false,
                message: "Please add at least one flow step."
            };
        }

        const keys = new Set();

        for (const step of formFlowSteps) {
            if (!step.key || !step.type) {
                return {
                    ok: false,
                    message: "Each flow step needs a key and type."
                };
            }

            if (!["scan", "photo", "qr"].includes(step.type)) {
                return {
                    ok: false,
                    message: `Invalid flow step type: ${step.type}`
                };
            }

            if (keys.has(step.key)) {
                return {
                    ok: false,
                    message: `Duplicate step key: ${step.key}`
                };
            }

            keys.add(step.key);
        }

        return {
            ok: true,
            steps: formFlowSteps.map(step => ({
                key: step.key,
                type: step.type,
                label: step.label || step.key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()),
                ...(step.description ? { description: step.description } : {})
            }))
        };
    };

    const handleSaveType = () => {
        if (!formTypeName) {
            toast.error("Name cannot be empty");
            return;
        }

        const flowValidation = validateFlow();

        if (!flowValidation.ok) {
            toast.error(flowValidation.message);
            return;
        }

        const headers = { 'token': localStorage.getItem('token') };

        const payload = {
            name: formTypeName,
            description: formTypeDesc,
            is_active: formTypeActive,
            requires_notification: formRequiresNotif,
            notification_time: formNotifTime,
            notify_teachers: selectedTeachers.map(t => t.value),
            flow_mode: formFlowMode,
            flow_steps: flowValidation.steps,
        };

        if (selectedType) {
            API.put(`/customAttendance/type/${selectedType.type_id}?is_portal=true`, payload, { headers })
                .then(res => {
                    if (res.data.response_code === 1000) {
                        toast.success("Type updated successfully");
                        closeTypeModal();
                        fetchAttendanceTypes();
                    } else {
                        toast.error(res.data.message);
                    }
                })
                .catch(() => toast.error("Error updating type"));
        } else {
            API.post(`/customAttendance/type?is_portal=true`, payload, { headers })
                .then(res => {
                    if (res.data.response_code === 1000) {
                        toast.success("Type created successfully");
                        closeTypeModal();
                        fetchAttendanceTypes();
                    } else {
                        toast.error(res.data.message);
                    }
                })
                .catch(() => toast.error("Error creating type"));
        }
    };

    const openDeleteModal = (type) => {
        setSelectedDeleteType(type);
        setIsDeleteModalOpen(true);
    };

    const closeDeleteModal = () => setIsDeleteModalOpen(false);

    const handleDeleteType = () => {
        const headers = { 'token': localStorage.getItem('token') };

        API.delete(`/customAttendance/type/${selectedDeleteType.type_id}?is_portal=true`, { headers })
            .then(res => {
                if (res.data.response_code === 1000) {
                    toast.success("Type deleted");
                    closeDeleteModal();
                    fetchAttendanceTypes();
                } else {
                    toast.error(res.data.message);
                }
            })
            .catch(() => toast.error("Error deleting type"));
    };

    return (
        <Fragment>
            <div className="container-fluid">
                <div className="card rounded-25">
                    <div className="card-header">
                        <h5>Custom Attendance Types</h5>
                        <button className="btn btn-primary float-end" onClick={openAddTypeModal}>
                            Add Type
                        </button>
                    </div>

                    <div className="card-body">
                        <DataTable
                            columns={columns}
                            data={attendanceTypes}
                            striped
                            persistTableHead
                            noDataComponent="No attendance types found. Click 'Add Type' to create one."
                        />
                    </div>
                </div>
            </div>

            <Modal isOpen={isTypeModalOpen} toggle={closeTypeModal} size="lg">
                <ModalHeader toggle={closeTypeModal}>
                    {selectedType ? "Edit Attendance Type" : "Add Attendance Type"}
                </ModalHeader>

                <ModalBody>
                    <div className="form-group">
                        <label>Name</label>
                        <input
                            className="form-control"
                            value={formTypeName}
                            onChange={e => setFormTypeName(e.target.value)}
                        />
                    </div>

                    <div className="form-group mt-2">
                        <label>Description</label>
                        <textarea
                            className="form-control"
                            rows="3"
                            value={formTypeDesc}
                            onChange={e => setFormTypeDesc(e.target.value)}
                        />
                    </div>

                    <div className="form-group mt-2">
                        <label>Status</label>
                        <select
                            className="form-control"
                            value={formTypeActive}
                            onChange={e => setFormTypeActive(Number(e.target.value))}
                        >
                            <option value={1}>Active</option>
                            <option value={0}>Inactive</option>
                        </select>
                    </div>

                    <hr />

                    <div className="form-group mt-3">
                        <label className="fw-bold">Attendance Flow</label>

                        <select
                            className="form-control"
                            value={formFlowMode}
                            onChange={e => handleFlowModeChange(e.target.value)}
                        >
                            <option value="single_step">Single Step - Photo Required</option>
                            <option value="multi_step">Multi Step - Dynamic Flow</option>
                        </select>

                        <small className="text-muted">
                            Existing normal attendance should use Single Step. Use Multi Step for scan → QR/photo flows.
                        </small>
                    </div>

                    <div className="form-group mt-3">
                        <label>Flow Preset</label>
                        <select
                            className="form-control"
                            onChange={e => applyPreset(e.target.value)}
                            defaultValue=""
                        >
                            <option value="" disabled>Select preset...</option>
                            {FLOW_PRESETS.map(preset => (
                                <option key={preset.value} value={preset.value}>
                                    {preset.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="mt-3">
                        <div className="d-flex align-items-center justify-content-between">
                            <label className="fw-bold mb-0">Flow Steps</label>

                            <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={handleAddStep}
                                disabled={formFlowMode === "single_step"}
                            >
                                Add Step
                            </button>
                        </div>

                        <div className="mt-2">
                            {formFlowSteps.map((step, index) => (
                                <div
                                    key={`${step.key}-${index}`}
                                    className="border rounded p-3 mb-2"
                                    style={{ background: "#fafafa" }}
                                >
                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                        <strong>Step {index + 1}</strong>

                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-danger"
                                            onClick={() => handleRemoveStep(index)}
                                            disabled={formFlowMode === "single_step"}
                                        >
                                            Remove
                                        </button>
                                    </div>

                                    <div className="row">
                                        <div className="col-md-4">
                                            <label>Type</label>
                                            <select
                                                className="form-control"
                                                value={step.type}
                                                disabled={formFlowMode === "single_step"}
                                                onChange={e => {
                                                    const newType = e.target.value;
                                                    const otherKeys = formFlowSteps
                                                        .filter((_, i) => i !== index)
                                                        .map(s => s.key);

                                                    updateFlowStep(index, {
                                                        type: newType,
                                                        key: makeSafeKey(step.label || newType, newType, otherKeys),
                                                    });
                                                }}
                                            >
                                                {STEP_TYPE_OPTIONS.map(option => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="col-md-4">
                                            <label>Label</label>
                                            <input
                                                className="form-control"
                                                value={step.label || ""}
                                                disabled={formFlowMode === "single_step"}
                                                onChange={e => updateFlowStep(index, { label: e.target.value })}
                                                placeholder="e.g. Check-in Photo"
                                            />
                                        </div>

                                        <div className="col-md-4">
                                            <label>Step Key</label>
                                            <input
                                                className="form-control"
                                                value={step.key || ""}
                                                disabled={formFlowMode === "single_step"}
                                                onChange={e => updateFlowStep(index, {
                                                    key: e.target.value
                                                        .toLowerCase()
                                                        .replace(/[^a-z0-9]+/g, "_")
                                                        .replace(/^_+|_+$/g, "")
                                                })}
                                                placeholder="e.g. checkin_photo"
                                            />
                                        </div>
                                    </div>

                                    <div className="mt-2">
                                        <label>Description</label>
                                        <input
                                            className="form-control"
                                            value={step.description || ""}
                                            disabled={formFlowMode === "single_step"}
                                            onChange={e => updateFlowStep(index, { description: e.target.value })}
                                            placeholder="Optional description for frontend"
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>

                        <small className="text-muted">
                            Step key must be unique. Example: two photo steps should be <strong>checkin_photo</strong> and <strong>verification_photo</strong>.
                        </small>
                    </div>

                    <hr />

                    <div className="form-group mt-3">
                        <label>Requires Notification</label>
                        <select
                            className="form-control"
                            value={formRequiresNotif}
                            onChange={e => setFormRequiresNotif(Number(e.target.value))}
                        >
                            <option value={1}>Yes</option>
                            <option value={0}>No</option>
                        </select>
                    </div>

                    {formRequiresNotif === 1 && (
                        <>
                            <div className="form-group mt-3">
                                <label>
                                    Notification Time (seconds)
                                    <span className="text-muted ms-1" style={{ fontSize: '0.9em' }}>
                                        — how long after check-in before alert is sent
                                    </span>
                                </label>

                                <input
                                    type="number"
                                    className="form-control"
                                    value={formNotifTime}
                                    min="0"
                                    onChange={e => setFormNotifTime(Number(e.target.value))}
                                    placeholder="e.g. 300 = 5 minutes"
                                />

                                {formNotifTime > 0 && (
                                    <small className="text-md text-black">
                                        ⏱ That’s approximately {(formNotifTime / 60).toFixed(1)} minutes after check-in.
                                    </small>
                                )}

                                {formNotifTime === 0 && (
                                    <small className="text-muted">
                                        If set to <strong>0</strong>, notifications are sent immediately after check-in.
                                    </small>
                                )}
                            </div>

                            <div className="form-group mt-3">
                                <label>Notify Teachers</label>
                                <Select
                                    isMulti
                                    options={teachers}
                                    value={selectedTeachers}
                                    onChange={(selected) => setSelectedTeachers(selected || [])}
                                    placeholder="Select teachers to notify..."
                                    classNamePrefix="react-select"
                                />

                                <small className="text-muted">
                                    Select one or more teachers who will receive notifications.
                                </small>
                            </div>
                        </>
                    )}
                </ModalBody>

                <ModalFooter>
                    <Button color="primary" onClick={handleSaveType}>
                        {selectedType ? "Update" : "Add"}
                    </Button>

                    <Button color="secondary" onClick={closeTypeModal}>
                        Cancel
                    </Button>
                </ModalFooter>
            </Modal>

            <Modal isOpen={isDeleteModalOpen} toggle={closeDeleteModal}>
                <ModalHeader toggle={closeDeleteModal}>Confirm Delete</ModalHeader>
                <ModalBody>
                    Delete type <strong>{selectedDeleteType?.name}</strong>?
                </ModalBody>
                <ModalFooter>
                    <Button color="danger" onClick={handleDeleteType}>
                        Yes, Delete
                    </Button>
                    <Button color="secondary" onClick={closeDeleteModal}>
                        Cancel
                    </Button>
                </ModalFooter>
            </Modal>
        </Fragment>
    );
};

export default CustomAttendanceTypePage;